<template>
    <section>
        <section v-if="state.isError">
            <div class="alert" v-text="$t('error.invalid_tx')" />
        </section>

        <t-breadcrumbs v-else class="t-breadcrumbs--page" v-bind:items="breadcrumbs"/>

        <div v-if="!state.isError" class="tx-preview-desktop-container">
            <div class="card mobile-tx-preview">
                <!-- Receipt-style header: the glyph on the left, the outcome and what happened on the right
                     with the amount or the action count under it; the band is washed with the status colour. -->
                <div class="tx-hero" v-bind:class="[`tx-hero--${heroStatus}`, { 'tx-hero--compact': !heroHasValue && !heroSummary }]">
                    <div class="tx-hero__glyph">
                        <mobile-overview-icon
                            v-bind:traceStatus="traceStatus"
                            v-bind:traceActions="simpleTxDetails"
                            v-bind:isPending="isPending"
                            v-bind:isTxSimple="isTxSimple"
                            v-bind:isTickTock="isTickTock"
                        />
                    </div>
                    <div class="tx-hero__body">
                        <mobile-status
                            v-bind:traceStatus="traceStatus"
                            v-bind:isPending="isPending"
                            v-bind:traceActions="simpleTxDetails"
                        />
                        <mobile-overview-value
                            v-if="heroHasValue"
                            v-bind:traceActions="simpleTxDetails"
                            v-bind:traceStatus="traceStatus"
                        />
                        <div v-else-if="heroSummary" class="tx-hero__summary">
                            <span>{{ heroSummary.actions }}</span>
                            <span v-if="heroSummary.failed" class="tx-hero__summary-failed">{{ heroSummary.failed }}</span>
                        </div>
                    </div>
                </div>
                <mobile-overview-address-list
                    v-bind:traceActions="simpleTxDetails"
                    v-bind:date="formattedDate"
                    v-bind:hash="hash"
                    v-bind:isTxSimple="isTxSimple"
                    v-bind:isTickTock="isTickTock"
                    v-bind:duration="duration"
                />
                <div class="mobile-tx-buttons-container">
                    <button class="mobile-tx-button" v-on:click="toggleShowDetails">
                        <icon-open-details />{{ $t('tx.details') }}
                    </button>
                    <button class="mobile-tx-button mobile-tx-button--share" v-on:click="shareTx">
                        <icon-share />{{ $t('tx.share') }}
                    </button>
                    <button class="mobile-tx-button mobile-tx-button--copy-link"
                        v-bind:class="{ 'mobile-tx-button--done': linkCopied }"
                        v-on:click="copyTxLink">
                        <icon-copy-link-success v-if="linkCopied" />
                        <icon-copy-link v-else />
                        {{ linkCopied ? $t('tx.link_copied') : $t('tx.copy_link') }}
                    </button>
                </div>
            </div>

            <!-- Desktop: the Overview / Details block unfolds to the right of the receipt, which
                 glides left to make room. The trace and the transaction data it opens stay below. -->
            <div v-if="!isMobile" class="tx-side-details"
                v-bind:class="{ 'tx-side-details--open': openDetails && !state.isError }">
                <div class="tx-side-details__inner">
                    <transaction-tabs
                        v-if="openDetails && !state.isError"
                        v-bind:traceId="traceId"
                        v-bind:timeStart="timeStart"
                        v-bind:traceActions="traceActions"
                        v-bind:traceEmpty="traceEmpty"
                        v-bind:trace="trace"
                        v-bind:isTickTock="isTickTock"
                        v-bind:duration="duration"
                        v-bind:warning="warning"
                        v-bind:isLargeTrace="isLargeTrace"
                        v-bind:hasMoreActions="hasMoreActions"
                        v-bind:isLoadingMoreActions="isLoadingMoreActions"
                        v-bind:traceTransactions="traceTransactions"
                        v-bind:traceAddressBook="traceAddressBook"
                        v-bind:traceMetadata="traceMetadata"
                        v-on:load-more-actions="loadMoreActions"
                    />
                </div>
            </div>
        </div>

        <transaction-tabs
            v-if="isMobile"
            style="margin-top: 18px"
            v-show="!state.isError && openDetails"
            v-bind:traceId="traceId"
            v-bind:timeStart="timeStart"
            v-bind:traceActions="traceActions"
            v-bind:traceEmpty="traceEmpty"
            v-bind:trace="trace"
            v-bind:isTickTock="isTickTock"
            v-bind:duration="duration"
            v-bind:warning="warning"
            v-bind:isLargeTrace="isLargeTrace"
            v-bind:hasMoreActions="hasMoreActions"
            v-bind:isLoadingMoreActions="isLoadingMoreActions"
            v-bind:traceTransactions="traceTransactions"
            v-bind:traceAddressBook="traceAddressBook"
            v-bind:traceMetadata="traceMetadata"
            v-on:load-more-actions="loadMoreActions"
        />

        <div class="trace-hidden" v-if="(isDiagramHidden || isLoadingFullTrace) && openDetails">
            <span class="muted">{{ $t('tx.large_trace_diagram') }}</span>
            <ui-mugen-scroll manual
                v-bind:handler="loadFullTrace"
                v-bind:shouldHandle="false"
                v-bind:showButton="true"
                v-bind:isLoading="isLoadingFullTrace"
                v-bind:label="$t('tx.large_trace_build_diagram')"/>
        </div>

        <div v-show="!state.isError && trace && !warning && !isDiagramHidden && !isSingleTransaction"
            v-bind:class="{'hide-trace': !openDetails }" class="trace">
            <Diagram
                v-bind:trace="trace"
                v-bind:connections="connections"
                v-on:node-clicked="handleNodeClicked"
            />
        </div>

        <template v-if="openDetails">
            <Transaction
                v-if="transactionData"
                ref="transactionDetails"
                class="tx-details"
                v-bind:transactionData="transactionData"
                v-bind:style="warning || isSingleTransaction ? 'margin-top: 18px' : ''"
            />

            <div class="open-details muted" v-else-if="Object.keys(trace).length > 0 && !state.isError">
                {{$t('tx.open_details')}}
            </div>

            <div class="transaction-button" v-if="state.showButton && !state.isLoading">
                <div class="transaction-button__items" v-on:click="handleButtonClicked">
                    <div class="transaction-button__icon">
                        <IconTxTypeOrdinary />
                    </div>
                    <div class="transaction-button__value">
                        <span>{{ $t('tx.button_transactions') }}</span>
                    </div>
                </div>
            </div>
        </template>
    </section>
</template>

<script>
import IconTxTypeOrdinary from '@img/icons/lucide/git-commit-horizontal.svg?inline';
import IconOpenDetails from '@img/icons/lucide/info.svg?inline';
import IconShare from '@img/icons/lucide/share-2.svg?inline';
import IconCopyLink from '@img/icons/lucide/link.svg?vue';
import IconCopyLinkSuccess from '@img/icons/lucide/check.svg?vue';
import { goToDevExplorerMixin } from '~/mixins';
import { base64ToHex, toBase64Web } from '~/utils.js';
import { getNftActionSenders, getTraceActions, getTransactionTraceV3 } from '~/api/toncenterV3';
import { copyTextFallback, formatTimestamp } from '~/helpers';
import { breadcrumbsJsonLd, shortenCrumb } from '~/breadcrumbs.js';
import { getTransactionByHashOrInMessageHash, getTransactionByInMsgHash } from '~/api';
import { showToast } from '~/toast';
import { mapState } from 'vuex';
import Transaction from './Transaction/Transaction.vue';
import TransactionTabs from './TransactionTabs.vue';
import MobileStatus from './Mobile/Status.vue';
import MobileOverviewIcon from './Mobile/OverviewIcon.vue';
import MobileOverviewValue from './Mobile/OverviewValue.vue';
import MobileOverviewAddressList from './Mobile/OverviewAddressList.vue';

// A trace with thousands of transactions weighs tens of megabytes and takes
// half a minute to lay out. Past this many actions the page stops asking for
// the whole thing and pages the actions in instead.
const LARGE_TRACE_ACTIONS = 100;

// Actions that describe how a trace ran rather than what it did: a gasless
// marker only says who paid for the request next to it.
const TECHNICAL_ACTION_TYPES = ['call_contract', 'contract_deploy', 'gasless_request'];

// Same portions the address page loads its history in.
const ACTIONS_FIRST_PAGE = 20;
const ACTIONS_NEXT_PAGE = 50;

export default {
    props: {
        hash: String,
    },

    data() {
        return {
            transactionInfo: {
                address: undefined,
                isSuccess: undefined,
                timestamp: undefined,
            },
            state: {
                isLoading: true,
                isError: false,
                showButton: false,
                msgHash: false,
            },
            hashData: {
                hex: undefined,
                lt: undefined,
            },
            trace: {},
            traceId: null,
            timeStart: null,
            connections: [],
            transaction: null,
            transactionData: null,
            firstTransaction: null,
            traceStatus: undefined,
            traceActions: undefined,
            // For the value flow tab: the trace transactions keyed by hash and
            // the lookups that came with them, see getTransactionTraceV3():
            traceTransactions: undefined,
            traceAddressBook: undefined,
            traceMetadata: undefined,
            traceEmpty: false,
            duration: undefined,
            isPending: false,
            isTickTock: false,
            warning: false,
            txRawData: undefined,
            traceFallback: false,
            linkCopied: false,
            isLargeTrace: false,
            actionsOffset: 0,
            hasMoreActions: false,
            isLoadingMoreActions: false,
            isDiagramRequested: false,
            isLoadingFullTrace: false,
            // The retry / pending poll that loadTrace has queued, if any:
            traceReloadTimer: null,
        };
    },

    computed: {
        // Home > the account the transaction is on > the hash. The account crumb
        // stays a skeleton until the transaction itself has loaded.
        breadcrumbs() {
            const address = this.transactionInfo.address;
            // The address book hands the address itself back when it has no name for it:
            const bookName = address ? this.$store.getters.getAddressName(address) : undefined;
            const accountName = bookName && bookName !== address ? bookName : shortenCrumb(address);

            return [
                { name: this.$t('breadcrumbs.home'), to: { name: 'index' } },
                { name: accountName, to: address ? { name: 'address', params: { address } } : undefined },
                { name: shortenCrumb(this.hash) },
            ];
        },

        heroStatus() {
            if (this.isPending) return 'pending';
            if (this.traceStatus === undefined) return 'skeleton';
            return this.traceStatus ? 'success' : 'failed';
        },

        // The amount line stays on a failed transfer too (struck through), so the receipt keeps
        // its shape either way; only complex traces and tick-tocks have nothing to put there.
        heroHasValue() {
            return this.isTxSimple && !this.isTickTock && this.traceStatus !== undefined;
        },

        // What goes under the kind when there is no single amount: how many actions the
        // trace has and how many of them went wrong. The count is open-ended while the
        // trace still has pages to load.
        heroSummary() {
            if (this.heroHasValue || this.isTickTock || this.traceStatus === undefined) return null;
            const count = this.traceActions?.length || 0;
            if (!count) return null;

            const failed = this.traceActions.filter(action => action.simple_preview?.is_success === false).length;
            const shown = this.hasMoreActions ? `${count}+` : count;

            return {
                actions: this.$tc('tx.hero_actions', count, { n: shown }),
                failed: failed ? this.$t('tx.hero_failed_actions', { n: failed }) : '',
            };
        },

        isGettingByMsgHash() {
            return this.$route.name === 'tx_by_msg_hash';
        },

        devExplorerUrl() {
            return `/transaction?account=${this.transactionInfo.address}&lt=${this.hashData.lt}&hash=${this.hashData.hex}`;
        },

        formattedDate() {
            if (!this.timeStart) return false;
            return formatTimestamp(this.timeStart);
        },

        isTxSimple() {
            if (!this.traceActions) return false;
            // Count how many actions are not associated with contracts
            const countRealActions = this.traceActions
                .filter(({ type }) => !TECHNICAL_ACTION_TYPES.includes(type))
                .length;

            return countRealActions === 1;
        },

        simpleTxDetails() {
            if (!this.traceActions) return [];

            const realActions = this.traceActions
                .filter(({ type }) => !TECHNICAL_ACTION_TYPES.includes(type));

            if (realActions.length > 0) return realActions;
            return this.traceActions;
        },

        openDetails() {
            if (!this.isTxSimple) return true;
            return this.openTxDetails;
        },

        isDiagramHidden() {
            return this.isLargeTrace && !this.isDiagramRequested;
        },

        // A trace of one transaction has nothing to draw: its details are shown straight away.
        // A pending one may still grow, so it keeps the diagram.
        isSingleTransaction() {
            return !this.isPending && !!this.trace?.transaction && !this.trace.children?.length;
        },
        ...mapState(['openTxDetails']),
    },

    created() {
        // Only the hex form of the hash is canonical. A base64 one is swapped in the
        // url and the page loads from the route watcher, the same way as any other
        // hash change — so it goes through the whole flow, action pages included.
        if (this.$route.params.hash.length !== 64) {
            this.$router.replace(this.$localizeRoute({
                name: this.$route.name,
                params: { hash: base64ToHex(this.$route.params.hash) },
            }));
            return;
        }

        this.initData();
    },

    beforeDestroy() {
        this.cancelTraceReload();
    },

    watch: {
        $route() {
            this.initData();
        },
        transaction() {
            if (this.transaction) this.loadTransaction();
        },
        'state.msgHash': function onMsgHash() {
            this.loadTrace();
        },
    },

    methods: {
        async initData() {
            // A poll queued for the previous transaction must not fire for this one:
            this.cancelTraceReload();

            // Reset fallback flag for new transaction
            this.traceFallback = false;
            this.isLargeTrace = false;
            this.isTickTock = false;
            this.traceEmpty = false;
            this.isDiagramRequested = false;
            this.actionsOffset = 0;
            this.hasMoreActions = false;

            // The trace is looked up by the hash in the url, not by anything
            // the transaction request returns, so it is started first and runs
            // alongside it instead of a round trip behind.
            const abortController = new AbortController();
            const tracePromise = this.loadTrace({ signal: abortController.signal });

            await this.loadData();

            const traceId = this.txRawData?.trace_id;

            // Race a cheap page of actions against the whole trace: the page
            // comes back long before the trace does, and the moment it shows
            // the trace is a big one the heavy request is dropped mid-flight.
            if (traceId) {
                await this.loadFirstActions(traceId);

                if (this.isLargeTrace) {
                    abortController.abort();
                }
            }

            await tracePromise;

            // Then we can show transaction data if there's warning (trace is too large)
            if (this.warning) {
                this.transactionData = this.txRawData;
            }

            // If trace loading failed but we have transaction data, show it anyway
            if (this.state.isError && this.txRawData) {
                this.state.isError = false;
                this.transactionData = this.txRawData;
                this.traceEmpty = true; // Mark trace as empty to hide skeleton loaders
                this.warning = true; // Use warning mode to skip trace diagram
                this.traceFallback = true; // Prevent loadTrace from resetting these values

                // Set basic info from transaction data when trace is unavailable
                this.timeStart = this.transactionInfo.timestamp;
                this.traceStatus = this.transactionInfo.isSuccess;
            }
        },

        async loadData() {
            this.state.isLoading = true;
            this.state.isError = false;

            this.transactionData = null;

            try {
                const apiMethod = this.isGettingByMsgHash ? getTransactionByInMsgHash : getTransactionByHashOrInMessageHash;
                const { transactions, address_book } = await apiMethod(this.hash);
                const tx = transactions[0];

                this.txRawData = tx;

                this.updateTransactionInfo(tx, address_book);
                this.fixInMsgHash(tx);
            } catch (error) {
                // this.state.isError = true;
                console.error(error);
            } finally {
                this.state.isLoading = false;
            }
        },

        async loadTrace({ reset = true, signal, force = false, diagramOnly = false } = {}) {
            // Don't reset or retry if we're already using fallback mode
            if (this.traceFallback) {
                return;
            }

            // A big trace is only ever fetched whole on an explicit request
            if (this.isLargeTrace && !force) {
                return;
            }

            if (reset) {
                this.state.isLoading = true;
                this.state.isError = false;
                this.transactionData = null;
                this.traceStatus = undefined;
                this.traceActions = undefined;
                this.traceTransactions = undefined;
                this.traceAddressBook = undefined;
                this.traceMetadata = undefined;
                this.trace = {};
                this.connections = [];
            }

            let newHash = base64ToHex(this.hash);
            // Get trace from toncenter
            try {
                if (this.$route.params.hash.length === 64) {
                    newHash = this.hash;
                }
                const res = await getTransactionTraceV3(newHash, !!this.state.msgHash, signal);
                if (!res?.trace || !res?.events?.actions) {
                    showToast('Error loading the transaction. Trying again');
                    this.scheduleTraceReload(3000, force);
                    return;
                }

                // Pulling every action back in is what made this page slow in
                // the first place, so the rows on screen are left alone and
                // only the tree the diagram draws is taken.
                if (diagramOnly) {
                    this.warning = res?.warning;
                    this.trace = res.trace;
                    this.connections = res.connections;
                    this.firstTransaction = res.trace.transaction?.hash;
                    return;
                }

                this.isPending = res.pending;
                this.traceActions = res.events.actions;
                this.traceTransactions = res.events.transactions;
                this.traceAddressBook = res.addressBook;
                this.traceMetadata = res.metadata;
                this.duration = res.duration;
                this.trace = res.trace;
                this.connections = res.connections;
                this.firstTransaction = res.trace.transaction?.hash;
                this.warning = res?.warning;

                if (this.isSingleTransaction) {
                    this.transactionData = res.trace.transaction;
                }

                if (res.events.actions.length === 0) {
                    this.traceEmpty = true;
                    if (this.firstTransaction) {
                        const checkTickTockTx = res?.events?.transactions[this.firstTransaction]?.description?.type;
                        if (checkTickTockTx && checkTickTockTx === 'tick_tock') {
                            this.isTickTock = true;
                            const tickTockTxDescription = res.events.transactions[this.firstTransaction].description;
                            this.traceStatus = tickTockTxDescription.compute_ph.success === true && !tickTockTxDescription?.action?.no_funds;
                        }
                    }
                }

                this.timeStart = res.series.timeStart * 1000;
                this.traceId = res.series.traceId;

                if (!this.isTickTock) {
                    // Without actions (toncenter's «unknown») the outcome comes from the transactions,
                    // by the same rule toncenter marks such a row failed in the history:
                    const hasFailedStatus = this.traceActions.length
                        ? this.traceActions.some(action => action.simple_preview.is_success === false)
                        : Object.values(res.events.transactions || {}).some(tx => tx.description?.aborted);

                    this.traceStatus = !hasFailedStatus;
                }
            } catch (error) {
                // The trace request is dropped on purpose once the actions page
                // shows the trace is a big one — that is not a failure
                if (error?.code === 'ERR_CANCELED') {
                    return;
                }

                this.state.isError = true;
                console.error(error);
            }

            if (this.isPending) {
                this.scheduleTraceReload(5000, force);
            }
        },

        // Retries and pending polls used to be bare setTimeouts, which kept
        // hitting the API for a page that had already been left behind.
        scheduleTraceReload(delay, force) {
            this.cancelTraceReload();
            this.traceReloadTimer = setTimeout(() => {
                this.traceReloadTimer = null;
                this.loadTrace({ reset: false, force });
            }, delay);
        },

        cancelTraceReload() {
            if (this.traceReloadTimer) {
                clearTimeout(this.traceReloadTimer);
                this.traceReloadTimer = null;
            }
        },

        // The first page comes with a size probe alongside it: one action asked
        // for from past the threshold tells us whether the whole trace is worth
        // asking for at all. Skipped when the full trace won the race — it
        // carries more than a page of actions does.
        async loadFirstActions(traceId) {
            try {
                const [page, tail] = await Promise.all([
                    getTraceActions(traceId, {
                        limit: ACTIONS_FIRST_PAGE,
                        account: this.hash,
                    }),
                    getTraceActions(traceId, {
                        limit: 1,
                        offset: LARGE_TRACE_ACTIONS,
                    }),
                ]);

                if (this.traceActions || page.actions.length === 0) {
                    return;
                }

                this.traceId = traceId;
                this.actionsOffset = page.count;
                this.isLargeTrace = tail.count > 0;
                this.hasMoreActions = page.count === ACTIONS_FIRST_PAGE;

                this.showActionsPage(page.actions);
                this.fillInNftSenders(page.actions);
            } catch (error) {
                console.error(error);
            }
        },

        async loadMoreActions() {
            const traceId = this.txRawData?.trace_id;

            if (this.isLoadingMoreActions || !this.hasMoreActions || !traceId) {
                return;
            }

            this.isLoadingMoreActions = true;

            try {
                const { actions, count } = await getTraceActions(traceId, {
                    limit: ACTIONS_NEXT_PAGE,
                    offset: this.actionsOffset,
                    account: this.hash,
                });

                this.actionsOffset += count;
                this.hasMoreActions = count === ACTIONS_NEXT_PAGE;
                this.traceActions = this.traceActions.concat(actions);

                if (this.hasFailedAction(actions)) {
                    this.traceStatus = false;
                }

                this.fillInNftSenders(actions);
            } catch (error) {
                showToast('Error loading the actions. Please try again');
                console.error(error);
            } finally {
                this.isLoadingMoreActions = false;
            }
        },

        // Asking for the trace itself is the expensive part, so it happens only
        // when someone actually wants the diagram. The action list keeps its
        // pages — the diagram simply takes the place of the panel offering it.
        async loadFullTrace() {
            // The diagram measures itself as it is drawn, so its container has
            // to be laid out before the trace arrives. It is revealed up front
            // and the panel stays over it until the drawing is done.
            this.isDiagramRequested = true;
            this.isLoadingFullTrace = true;

            try {
                await this.loadTrace({ force: true, reset: false, diagramOnly: true });
            } finally {
                this.isLoadingFullTrace = false;
            }

            if (this.warning || !Object.keys(this.trace).length) {
                this.isDiagramRequested = false;
                showToast('The diagram is not available for this trace');
            }
        },

        // Everything the header shows normally comes from the full trace. With
        // only a page of actions in hand it comes from the actions themselves
        // and from the transaction the page was opened for.
        showActionsPage(actions) {
            this.traceActions = actions;
            this.traceEmpty = false;
            this.timeStart = actions[0].start_utime * 1000;
            this.duration = actions[0].trace_end_utime - actions[0].start_utime;

            if (this.isLargeTrace) {
                this.traceStatus = !!this.transactionInfo.isSuccess && !this.hasFailedAction(actions);
            }
        },

        // A page of actions names only one side of an NFT route, because
        // toncenter leaves the sender out of the payload. The rows are already
        // on screen by now — the column fills in when the lookup lands.
        async fillInNftSenders(actions) {
            try {
                const patches = await getNftActionSenders(actions);

                if (!patches.size || !this.traceActions) {
                    return;
                }

                this.traceActions = this.traceActions.map((row) => {
                    const patch = patches.get(row.action_id);

                    return patch
                        ? { ...row, simple_preview: { ...row.simple_preview, ...patch } }
                        : row;
                });
            } catch (error) {
                console.error(error);
            }
        },

        hasFailedAction(actions) {
            return actions.some(action => action.simple_preview?.is_success === false);
        },

        async loadTransaction() {
            try {
                /* eslint-disable-next-line no-inner-declarations */
                function findTransaction(trace, targetHash) {
                    if (trace.transaction.hash === targetHash) return trace.transaction;
                    // eslint-disable-next-line no-restricted-syntax
                    for (const child of trace.children || []) {
                        const result = findTransaction(child, targetHash);
                        if (result) return result;
                    }
                    return null;
                }

                this.transactionData = findTransaction(this.trace, this.transaction) || null;
            } catch (error) {
                console.error(error);
            }
        },

        updateTransactionInfo(tx, addressBook) {
            this.transactionInfo.address = addressBook[tx.account].user_friendly;

            const newWalletTxSuccess = tx.description?.action?.result_code === undefined && tx.description?.compute_ph?.exit_code === undefined;
            const executionSuccess = tx.description?.action?.action_result_code !== null && parseInt(tx.description?.action?.result_code, 10) <= 1;
            this.transactionInfo.isSuccess = newWalletTxSuccess || executionSuccess;
            this.transactionInfo.timestamp = parseInt(`${tx.now}000`, 10);

            this.hashData.hex = base64ToHex(tx.hash);
            this.hashData.lt = tx.lt;
        },

        fixInMsgHash(tx) {
            if (tx?.in_msg?.hash && toBase64Web(tx.in_msg.hash) === toBase64Web(this.hash)) {
                this.state.msgHash = true;
                this.$router.replace(this.$localizeRoute({
                    name: 'tx_by_msg_hash',
                    params: { hash: toBase64Web(this.hash) },
                }));
            }
        },

        handleNodeClicked(data) {
            if (data?.transaction?.emulated) return;
            this.transaction = data.transaction.hash;
            this.state.showButton = false;
            // The details card sits below the trace, which on a long trace
            // means below the fold: bring it on screen once it has rendered.
            this.$nextTick(this.revealTransactionDetails);
        },

        revealTransactionDetails() {
            const el = this.$refs.transactionDetails?.$el;
            if (!el) return;

            const { top } = el.getBoundingClientRect();
            const headerHeight = 56;
            // Already in view (below the header, above the fold): don't move the page.
            if (top >= headerHeight && top <= window.innerHeight * 0.6) return;

            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        },

        handleButtonClicked() {
            this.state.showButton = false;
            this.transaction = this.firstTransaction;
        },

        toggleShowDetails() {
            this.$store.commit('switchOpenTxDetails');
        },

        async shareTx() {
            const url = `https://tonscan.org/tx/${this.hash}`;
            const shareData = {
                title: `Transaction ${this.hash}`,
                url,
            };
            try {
                await navigator.share(shareData);
            } catch (err) {
                try {
                    await navigator.clipboard.writeText(url);
                } catch {
                    copyTextFallback(url);
                }
                showToast('Link copied!');
            }
        },

        async copyTxLink() {
            const url = `https://tonscan.org/tx/${this.hash}`;
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                copyTextFallback(url);
            }
            showToast('Link copied!');
            this.linkCopied = true;
            setTimeout(() => {
                this.linkCopied = false;
            }, 1000);
        },
    },

    metaInfo() {
        return {
            title: this.$t('tx.meta.title', { hash: this.hash }),
            meta: [
                { name: 'robots', content: 'noindex' },
                {
                    vmid: 'description', name: 'description',
                    content: this.$t('tx.meta.description', { hash: this.hash }),
                },
            ],
            script: [breadcrumbsJsonLd(this, this.breadcrumbs)].filter(Boolean),
        };
    },

    components: {
        // Pulls in all of d3 — kept out of the main bundle and fetched
        // alongside the trace it draws:
        Diagram: () => import(/* webpackChunkName: "tx-diagram" */ './Diagram/Diagram.vue'),
        IconTxTypeOrdinary,
        Transaction,
        TransactionTabs,
        MobileStatus,
        MobileOverviewIcon,
        MobileOverviewValue,
        MobileOverviewAddressList,
        IconOpenDetails,
        IconShare,
        IconCopyLink,
        IconCopyLinkSuccess,
    },

    mixins: [goToDevExplorerMixin],
};
</script>

<style lang="scss">
.tx-preview-desktop-container {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
}

// Side panel for the details card: grows from zero width so the receipt slides left smoothly.
.tx-side-details {
    display: none;
}

@media screen and (min-width: 900px) {
    .tx-preview-desktop-container {
        flex-direction: row;
        align-items: flex-start;
        justify-content: center;
    }

    .mobile-tx-preview {
        flex: 0 0 auto;
    }

    .tx-side-details {
        display: block;
        flex: 1 1 0;
        min-width: 0;
        max-width: 0;
        margin-left: 0;
        opacity: 0;
        overflow: hidden;
        transform: translateX(-12px);
        transition: max-width .5s cubic-bezier(.2, .8, .2, 1),
                    margin-left .5s cubic-bezier(.2, .8, .2, 1),
                    opacity .35s ease .1s,
                    transform .5s cubic-bezier(.2, .8, .2, 1);

        &--open {
            max-width: 700px;
            margin-left: 18px;
            opacity: 1;
            transform: none;
        }

        // Keeps the card at its natural width while the panel is still narrow,
        // so the content doesn't reflow during the slide:
        &__inner {
            min-width: 420px;
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .tx-side-details {
        transition: none;
    }
}
.hide-trace {
    pointer-events: none;
    opacity: 0;
}
// Keeps the card clear of the sticky header when it is scrolled into view after a node click.
.tx-details {
    scroll-margin-top: 66px;
}
.trace-hidden {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    font-size: 14px;
}
.tx-status {
    display: flex;
    align-items: center;
    font-weight: 500;

    &--failed {
        color: var(--page-tx-status-error-color);
    }

    &--success {
        color: var(--page-tx-status-success-color);
    }

    &__icon {
        margin-right: 5px;
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
}

.tx-flow-schematics {
    display: flex;
    align-items: center;
    border: 1px solid var(--page-tx-flow-diagram-border-color);
    overflow: hidden;
    border-radius: 8px;
}

.tx-flow-schematics-step {
    display: flex;
    align-items: center;

    &__inner {
        padding: 4px 10px;
        display: flex;
        flex-direction: column;
    }

    &__phase {
        font-size: 12px;
    }

    &::after {
        content: '';
        display: block;
        width: 40px;
        height: 40px;
        border-color: var(--page-tx-flow-diagram-border-color);
        border-width: 0 1px 1px 0;
        border-style: solid;
        transform: rotate(-45deg);
        margin: 0 8px 0 -28px;
        user-select: none;
        pointer-events: none;
    }

    &:last-child::after {
        display: none;
    }
}

.card-timeago {
    color: var(--body-muted-text-color);
}

.trace {
    margin: 20px 0;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.trace::-webkit-scrollbar {
    display: none;
}

.transaction-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;

    &__items {
        box-shadow: 0 0.5rem 1.2rem var(--card-box-shadow-color);
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        border: 1px solid var(--body-light-muted-color);
        padding: 0 12px;
        line-height: 36px;
        border-radius: 7px;
        color: var(--body-text-color);
        transition: all 0.2s;
    }

    &__items:hover {
        border: 1px solid var(--body-muted-text-color);
        color: var(--body-text-color);
        text-decoration: none;
    }

    &__icon {
        width: 24px;
        color: var(--body-text-color);

        svg {
            display: block;
            fill: currentColor;
        }
    }

    &__value {
        color: var(--body-text-color);
        font-size: 14px;
        text-transform: uppercase;
    }
}

.open-details {
    text-align: center;
    font-size: 14px;
}

.mobile-tx-preview {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0 12px 12px;
    align-items: center;
    box-sizing: border-box;
    overflow: hidden;

    &__time {
        font-size: 14px;
        margin: 6px 0;
    }
}

@media screen and (min-width: 600px) {
    .mobile-tx-preview {
        max-width: 480px;
    }
}

// --- Receipt header ---
.tx-hero {
    display: flex;
    align-items: center;
    gap: 14px;
    width: calc(100% + 24px);
    margin: 0 -12px 4px;
    padding: 18px 16px;
    box-sizing: border-box;
    border-bottom: 1px solid var(--card-row-separator);
    background: linear-gradient(180deg, var(--body-light-muted-color), transparent);

    // The wash is strongest right behind the glyph and dies out towards the amount,
    // so the solid disc reads as the light source of the band:
    &--success {
        background:
            radial-gradient(140px 120px at 44px 50%, var(--badge-green-background-color), transparent 70%),
            linear-gradient(180deg, var(--badge-green-background-color), transparent);
    }
    &--failed {
        background:
            radial-gradient(140px 120px at 44px 50%, var(--badge-red-background-color), transparent 70%),
            linear-gradient(180deg, var(--badge-red-background-color), transparent);
    }

    // The disc takes the status colour with a white glyph; the amount stays neutral
    // so the band is not green on green on green.
    &--success .tx-mobile-overview-icon {
        color: #fff;
        background: var(--green-bright);
        box-shadow: 0 10px 24px -8px rgba(76, 212, 113, .55);
    }
    &--failed .tx-mobile-overview-icon {
        color: #fff;
        background: var(--red-bright);
        box-shadow: 0 10px 24px -8px rgba(255, 107, 96, .55);
    }
    .mobile-tx-value .ui-inline-jetton--green {
        color: var(--body-text-color);
    }

    // The amount that did not move: still there, but crossed out and stepped back.
    &--failed .mobile-tx-value {
        color: var(--body-muted-text-color);
        text-decoration: line-through;
        text-decoration-thickness: 1.5px;
    }

    &__glyph {
        flex-shrink: 0;
        display: flex;
        align-items: center;
    }

    &__body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    // "5 actions · 3 failed": the failed part carries the status colour, the rest stays quiet.
    &__summary {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 0;
        margin-top: 2px;
        font-size: 15px;
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--body-muted-text-color);
    }
    &__summary-failed {
        color: var(--red-bright);
        &::before {
            content: '·';
            margin: 0 6px;
            color: var(--body-muted-text-color);
        }
    }

    // Everything inside the band sits on the left and shrinks to the row:
    .tx-mobile-overview {
        margin: 0;
    }
    .tx-mobile-overview-icon {
        width: 56px;
        height: 56px;
        svg {
            width: 26px;
            height: 26px;
        }
    }
    .tx-mobile-overview-nft {
        width: 56px;
        height: 56px;
        border-radius: 14px;
        svg {
            width: 26px;
            height: 26px;
        }
    }
    .tx-mobile-overview-swap {
        gap: 0;
    }
    .tx-mobile-overview-swap-logo {
        width: 44px;
        height: 44px;
        border-width: 3px;
        &--left {
            transform: translateX(8px);
        }
        &--right {
            transform: translateX(-8px);
        }
    }
    .mobile-tx-value {
        margin: 2px 0 0;
        font-family: inherit;
        font-size: 24px;
        font-weight: 600;
        letter-spacing: -0.02em;
        line-height: 1.15;
        text-align: left;
        &--swap {
            font-size: 18px;
            // A swap too long for the row carries the receiving side over to a
            // second line, right after the arrow:
            .ui-inline-jetton {
                flex-wrap: wrap;
                row-gap: 2px;
            }
        }
        &--message-container {
            justify-content: flex-start;
        }
        &--message {
            margin: 10px 0 0;
            border-radius: 3px 14px 14px 14px;
            font-size: 14px;
        }
    }

    // One-line band (nothing to show under the kind): the disc shrinks so it is not
    // a big light source next to a single caption.
    &--compact {
        padding: 14px 16px;
        gap: 12px;
        .tx-mobile-overview-icon,
        .tx-mobile-overview-nft {
            width: 44px;
            height: 44px;
            svg {
                width: 20px;
                height: 20px;
            }
        }
    }
}

.mobile-tx-buttons-container {
    width: 100%;
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr 1fr;
    box-sizing: border-box;
    margin-top: 18px;
}

.mobile-tx-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    background-color: var(--body-light-muted-color);
    color: var(--body-text-color);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.01em;
    cursor: pointer;
    text-decoration: none;
    user-select: none;
    transition: background-color .15s ease, color .15s ease, transform .1s ease;

    svg {
        width: 16px;
        height: 16px;
        color: var(--body-muted-text-color);
        transition: color .15s ease;
    }

    &:hover {
        background-color: var(--icon-button-hover-background);
        svg {
            color: var(--body-text-color);
        }
    }
    &:active {
        transform: scale(.97);
    }
    &:focus-visible {
        outline: 2px solid var(--blue-bright);
        outline-offset: 2px;
    }

    // Link copied: the button turns green for a moment instead of a toast-only confirmation.
    &--done {
        &, &:hover {
            color: var(--green-bright);
            background-color: var(--badge-green-background-color);
            svg {
                color: var(--green-bright);
            }
        }
    }

    &--copy-link {
        display: none;
    }
}

@media screen and (min-width: 600px) {
    .mobile-tx-button--share {
        display: none;
    }
    .mobile-tx-button--copy-link {
        display: flex;
    }
}
</style>
